import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import * as Actions from '../redux/actions';
import Loading from '../Loading';
import ProductInfoModal from '../lookup/ProductInfoModal';
import {
  PRODUCT_TYPES,
  STATIC_DELIVERY_OPTIONS,
  parseOptionList,
  defaultSize,
  defaultConcentration,
  formatQuantity,
  cartLineDetails,
  cartTotals,
  orderToWizardState,
} from './orderUtils';

const emptyConfig = () => ({
  productType: null,
  recipientType: null,
  accountSelected: null,
  method: 'Pickup',
  destination: '',
  pickupDate: '',
  charge: false,
  notes: '',
});

const OrderWizard = (props) => {
  const {
    authToken,
    fetchOrderProducts,
    fetchOrderDeliveryOptions,
    saveOrder,
    fetchOrderHistory,
    fetchOrderDrafts,
    clearActiveOrderDraft,
    fetchInfo,
    clearInfo,
    activeOrderDraft,
    orderProducts,
    accounts,
    loading,
    loadingMessage,
  } = props;

  const [config, setConfig] = useState(emptyConfig);
  const [cart, setCart] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [search, setSearch] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [productInfoId, setProductInfoId] = useState(null);
  const [cartSheetOpen, setCartSheetOpen] = useState(false);
  const [deliveryOptions, setDeliveryOptions] = useState(STATIC_DELIVERY_OPTIONS);
  const draftIdRef = useRef(null);
  const lastHydratedId = useRef(null);
  const skipAutosave = useRef(false);
  const autosaveTimer = useRef(null);
  const savingDraft = useRef(false);
  const pendingAutosave = useRef(false);
  const pendingSubmit = useRef(false);

  const configReady = config.productType != null && config.recipientType != null
    && (config.recipientType !== 'customer' || config.accountSelected != null);

  useEffect(() => {
    if (!authToken || !configReady || config.productType == null) return;
    fetchOrderProducts(authToken, config.productType);
  }, [authToken, config.productType, configReady, fetchOrderProducts]);

  useEffect(() => {
    if (!authToken || !configReady) return;

    const customerId = config.recipientType === 'customer' ? config.accountSelected?.id : null;
    fetchOrderDeliveryOptions(authToken, customerId, (options) => {
      const next = (options && options.length) ? options : STATIC_DELIVERY_OPTIONS;
      setDeliveryOptions(next);
      const values = next.map((o) => String(o.value));
      setConfig((prev) => {
        if (values.includes(String(prev.destination))) return prev;
        return { ...prev, destination: values[0] ?? '' };
      });
    });
  }, [
    authToken,
    configReady,
    config.recipientType,
    config.accountSelected?.id,
    fetchOrderDeliveryOptions,
  ]);

  const vendors = useMemo(() => {
    const map = new Map();
    (orderProducts || []).forEach((p) => {
      if (p.vendor?.id) map.set(p.vendor.id, p.vendor);
    });
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [orderProducts]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (orderProducts || []).filter((p) => {
      if (vendorId && String(p.vendor_id) !== String(vendorId)) return false;
      if (!q) return true;
      const hay = (p.search_string || `${p.model} ${p.description}`).toLowerCase();
      return hay.includes(q);
    });
  }, [orderProducts, search, vendorId]);

  const customerMatches = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return [];
    return (accounts || [])
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [accounts, customerQuery]);

  useEffect(() => {
    if (!cart.length) setCartSheetOpen(false);
  }, [cart.length]);

  useEffect(() => {
    if (!cartSheetOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCartSheetOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [cartSheetOpen]);

  useEffect(() => {
    const order = activeOrderDraft;
    if (!order?.id) return;
    if (lastHydratedId.current === order.id) return;
    const mapped = orderToWizardState(order);
    if (!mapped) return;
    skipAutosave.current = true;
    lastHydratedId.current = order.id;
    draftIdRef.current = mapped.draftId;
    setDraftId(mapped.draftId);
    setConfig(mapped.config);
    setCart(mapped.cart);
    setSearch('');
    setVendorId('');
    setSubmitted(false);
    setCartSheetOpen(false);
    setCustomerQuery(mapped.config.accountSelected?.name || '');
    setDeliveryOptions(STATIC_DELIVERY_OPTIONS);
  }, [activeOrderDraft]);

  const openCartSheet = () => setCartSheetOpen(true);
  const closeCartSheet = () => setCartSheetOpen(false);

  const totals = useMemo(() => cartTotals(cart), [cart]);

  const setConfigField = (patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  };

  const selectProductType = (key) => {
    setConfigField({ productType: key });
    setCart([]);
    setSearch('');
    setVendorId('');
  };

  const selectRecipientType = (type) => {
    setConfigField({
      recipientType: type,
      accountSelected: type === 'personal' ? null : config.accountSelected,
      charge: type === 'customer' ? config.charge : false,
    });
    if (type === 'personal') setCustomerQuery('');
  };

  const selectCustomer = (customer) => {
    setConfigField({ accountSelected: customer, recipientType: 'customer' });
    setCustomerQuery(customer.name);
  };

  const openProductInfo = (product) => {
    setProductInfoId(product.id);
    fetchInfo(authToken, product.id, 'p', null, { updateHistory: false });
  };

  const closeProductInfo = () => {
    setProductInfoId(null);
    clearInfo();
  };

  const addToCart = (product) => {
    if (!product.active) return;
    setCart((prev) => [...prev, {
      key: `${product.id}-${Date.now()}`,
      item: product,
      option: product.variants?.length === 1 ? product.variants[0] : null,
      size: defaultSize(product),
      concentration: defaultConcentration(product),
      quantity: 1,
    }]);
  };

  const updateLine = (key, patch) => {
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const changeQuantity = (key, delta) => {
    setCart((prev) => prev
      .map((l) => (l.key === key ? { ...l, quantity: l.quantity + delta } : l))
      .filter((l) => l.quantity > 0));
  };

  const snapshotRef = useRef({ config, cart, configReady, submitted, authToken });
  snapshotRef.current = { config, cart, configReady, submitted, authToken };

  const resetOrder = () => {
    clearTimeout(autosaveTimer.current);
    skipAutosave.current = true;
    pendingAutosave.current = false;
    pendingSubmit.current = false;
    lastHydratedId.current = null;
    draftIdRef.current = null;
    setConfig(emptyConfig());
    setCart([]);
    setDraftId(null);
    setSearch('');
    setVendorId('');
    setCustomerQuery('');
    setSubmitted(false);
    setDeliveryOptions(STATIC_DELIVERY_OPTIONS);
    clearActiveOrderDraft();
  };

  const buildSavePayload = useCallback((asDraft = false) => {
    const { config: cfg, cart: lines } = snapshotRef.current;
    return {
      id: draftIdRef.current || undefined,
      draft: asDraft,
      config: {
        accountSelected: cfg.recipientType === 'customer' ? cfg.accountSelected : null,
        notes: cfg.notes,
        method: cfg.method,
        charge: cfg.charge,
        destination: cfg.method === 'Delivery' ? cfg.destination : null,
        pickupDate: cfg.method === 'Pickup' ? cfg.pickupDate : null,
      },
      cart: lines.map((line) => ({
        item: { id: line.item.id },
        option: line.option ? { id: line.option.id } : null,
        size: line.size || null,
        concentration: line.concentration || null,
        quantity: line.quantity,
      })),
    };
  }, []);

  const submitOrder = useCallback(() => {
    const snap = snapshotRef.current;
    if (!snap.cart.length || !snap.configReady || !snap.authToken) return;
    saveOrder(snap.authToken, buildSavePayload(false), () => {
      setCartSheetOpen(false);
      lastHydratedId.current = null;
      draftIdRef.current = null;
      setDraftId(null);
      setSubmitted(true);
      fetchOrderHistory(snap.authToken);
      fetchOrderDrafts(snap.authToken);
    });
  }, [saveOrder, fetchOrderHistory, fetchOrderDrafts, buildSavePayload]);

  const flushDraftSave = useCallback(() => {
    const snap = snapshotRef.current;
    if (!snap.authToken || snap.submitted || !snap.configReady) return;
    if (!snap.cart.length && !draftIdRef.current) return;
    if (savingDraft.current) {
      pendingAutosave.current = true;
      return;
    }

    savingDraft.current = true;
    saveOrder(
      snap.authToken,
      buildSavePayload(true),
      (order) => {
        if (order?.id) {
          lastHydratedId.current = order.id;
          draftIdRef.current = order.id;
          setDraftId(order.id);
        }
      },
      {
        silent: true,
        onSettled: () => {
          savingDraft.current = false;
          if (pendingSubmit.current) {
            pendingSubmit.current = false;
            pendingAutosave.current = false;
            submitOrder();
            return;
          }
          if (pendingAutosave.current) {
            pendingAutosave.current = false;
            flushDraftSave();
          }
        },
      }
    );
  }, [saveOrder, submitOrder, buildSavePayload]);

  useEffect(() => {
    if (submitted || !configReady || !authToken) return;
    if (!cart.length && !draftIdRef.current) return;
    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }

    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(flushDraftSave, 700);
    return () => clearTimeout(autosaveTimer.current);
  }, [
    submitted,
    configReady,
    authToken,
    config.productType,
    config.recipientType,
    config.accountSelected?.id,
    config.method,
    config.destination,
    config.pickupDate,
    config.charge,
    config.notes,
    cart,
    flushDraftSave,
  ]);

  useEffect(() => () => clearTimeout(autosaveTimer.current), []);

  const handleSubmit = () => {
    if (!cart.length || !configReady) return;
    clearTimeout(autosaveTimer.current);
    pendingAutosave.current = false;
    if (savingDraft.current) {
      pendingSubmit.current = true;
      return;
    }
    submitOrder();
  };

  return (
    <div className="order-wizard">
      <Loading loading={loading} loadingMessage={loadingMessage} />

      <header
        className={`order-page-header${configReady || submitted ? ' order-page-header--hidden' : ''}`}
        aria-hidden={configReady || submitted}
      >
        <div className="order-page-header-main">
          <h1 className="order-page-title">
            {draftId ? `Draft #${draftId}` : 'New order'}
          </h1>
          <p className="order-page-subtitle">
            Start by choosing what you are ordering and who it is for.
          </p>
        </div>
      </header>

      {submitted ? (
        <section className="order-panel order-success-panel">
          <CheckCircleOutlineIcon className="order-success-icon" aria-hidden="true" />
          <h2>Order submitted</h2>
          <p>Your order has been sent successfully.</p>
          <button type="button" className="order-btn order-btn--primary" onClick={resetOrder}>
            Start another order
          </button>
        </section>
      ) : (
        <>
          <section className="order-panel order-setup-panel" aria-label="Order setup">
            <div className="order-setup-grid">
              <div className="order-setup-field">
                <span className="order-field-label">Product type</span>
                <div className="order-segmented" role="group" aria-label="Product type">
                  {PRODUCT_TYPES.map((type) => (
                    <button
                      key={type.key}
                      type="button"
                      className={`order-segment${config.productType === type.key ? ' order-segment--active' : ''}`}
                      onClick={() => selectProductType(type.key)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="order-setup-field">
                <span className="order-field-label">Order for</span>
                <div className="order-segmented" role="group" aria-label="Recipient type">
                  <button
                    type="button"
                    className={`order-segment${config.recipientType === 'customer' ? ' order-segment--active' : ''}`}
                    onClick={() => selectRecipientType('customer')}
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    className={`order-segment${config.recipientType === 'personal' ? ' order-segment--active' : ''}`}
                    onClick={() => selectRecipientType('personal')}
                  >
                    Personal
                  </button>
                </div>
              </div>

              {config.recipientType === 'customer' ? (
                <div className="order-setup-field order-setup-field--wide">
                  <span className="order-field-label">Customer</span>
                  <div className="order-customer-search">
                    <input
                      type="text"
                      autoFocus={true}
                      className="order-input browser-default"
                      placeholder="Search by customer name…"
                      value={customerQuery}
                      onChange={(e) => {
                        setCustomerQuery(e.target.value);
                        if (config.accountSelected && e.target.value !== config.accountSelected.name) {
                          setConfigField({ accountSelected: null });
                        }
                      }}
                    />
                    {customerMatches.length > 0 && !config.accountSelected ? (
                      <ul className="order-customer-results browser-default">
                        {customerMatches.map((c) => (
                          <li key={c.id}>
                            <button type="button" onClick={() => selectCustomer(c)}>{c.name}</button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          {configReady ? (
            <div className="order-workspace">
              <section className="order-panel order-catalog-panel" aria-label="Product catalog">
                <div className="order-panel-head">
                  <div>
                    <h2 className="order-panel-title">Products</h2>
                    <p className="order-panel-desc">{filteredProducts.length} available</p>
                  </div>
                </div>

                <div className="order-catalog-toolbar">
                  <label className="order-search">
                    <SearchIcon className="order-search-icon" aria-hidden="true" />
                    <input
                      type="search"
                      className="order-input order-input--search browser-default"
                      placeholder="Search by model or description…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </label>
                  <select
                    className="order-input order-vendor-select browser-default"
                    value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    aria-label="Filter by vendor"
                  >
                    <option value="">All vendors</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>

                {filteredProducts.length === 0 ? (
                  <div className="order-empty-state">
                    <p>No products match your search.</p>
                    <button
                      type="button"
                      className="order-btn order-btn--ghost"
                      onClick={() => { setSearch(''); setVendorId(''); }}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <ul className="order-product-list browser-default">
                    {filteredProducts.map((p) => (
                      <li key={p.id}>
                        <article className={`order-product-card${!p.active ? ' order-product-card--inactive' : ''}`}>
                          <div className="order-product-card-main">
                            <div className="order-product-card-head">
                              <h3 className="order-product-card-model">{p.model}</h3>
                              {p.vendor?.name ? (
                                <span className="order-product-card-vendor">{p.vendor.name}</span>
                              ) : null}
                            </div>
                            <p className="order-product-card-desc">
                              {!p.active ? <span className="order-badge order-badge--warn">Unavailable</span> : null}
                              {p.description}
                            </p>
                          </div>
                          <div className="order-product-card-actions">
                            <button
                              type="button"
                              className="order-icon-btn order-icon-btn--info"
                              onClick={() => openProductInfo(p)}
                              aria-label={`Product info for ${p.model}`}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </button>
                            <button
                              type="button"
                              className="order-icon-btn order-icon-btn--add"
                              disabled={!p.active}
                              onClick={() => addToCart(p)}
                              aria-label={`Add ${p.model} to cart`}
                            >
                              <AddShoppingCartIcon fontSize="small" />
                            </button>
                          </div>
                        </article>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <aside
                id="order-cart-panel"
                className={`order-panel order-cart-panel${cartSheetOpen ? ' order-cart-panel--open' : ''}`}
                aria-label="Current order"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="order-cart-sheet-handle"
                  aria-label="Close cart"
                  onClick={closeCartSheet}
                />
                <div className="order-panel-head">
                  <div>
                    <h2 className="order-panel-title">
                      {config.recipientType === 'customer' && config.accountSelected?.name
                        ? config.accountSelected.name
                        : 'Your order'}
                    </h2>
                    <p className="order-panel-desc">
                      {totals.lines === 0
                        ? 'No items yet'
                        : `${totals.lines} line${totals.lines === 1 ? '' : 's'} · ${totals.items} item${totals.items === 1 ? '' : 's'}`}
                    </p>
                  </div>
                </div>

                {cart.length === 0 ? (
                  <div className="order-cart-empty">
                    <AddShoppingCartIcon aria-hidden="true" />
                    <p>Add products from the catalog to build your order.</p>
                  </div>
                ) : (
                  <ul className="order-cart-lines browser-default">
                    {cart.map((line) => (
                      <li key={line.key} className="order-cart-line">
                        <div className="order-cart-line-top">
                          <div className="order-cart-line-info">
                            <span className="order-cart-line-name">{line.item.model}</span>
                            {cartLineDetails(line) ? (
                              <span className="order-cart-line-detail">{cartLineDetails(line)}</span>
                            ) : null}
                            <span className="order-cart-line-meta">{formatQuantity(line)}</span>
                          </div>
                          <div className="order-qty-controls" aria-label="Quantity">
                            <button type="button" onClick={() => changeQuantity(line.key, -1)} aria-label="Decrease quantity">
                              <RemoveIcon fontSize="small" />
                            </button>
                            <span>{line.quantity}</span>
                            <button type="button" onClick={() => changeQuantity(line.key, 1)} aria-label="Increase quantity">
                              <AddIcon fontSize="small" />
                            </button>
                          </div>
                        </div>

                        {line.item.variants?.length > 0 ? (
                          <div className="order-line-options">
                            <span className="order-line-options-label">Option</span>
                            <div className="order-option-chips">
                              {line.item.variants.map((v) => (
                                <button
                                  key={v.id}
                                  type="button"
                                  className={`order-option-chip${line.option?.id === v.id ? ' order-option-chip--active' : ''}`}
                                  onClick={() => updateLine(line.key, { option: line.option?.id === v.id ? null : v })}
                                >
                                  {v.code}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {parseOptionList(line.item.size_options).length > 0 ? (
                          <div className="order-line-options">
                            <span className="order-line-options-label">Size</span>
                            <div className="order-option-chips">
                              {parseOptionList(line.item.size_options).map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  className={`order-option-chip${line.size === s ? ' order-option-chip--active' : ''}`}
                                  onClick={() => updateLine(line.key, { size: s })}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {parseOptionList(line.item.concentration).length > 0 ? (
                          <div className="order-line-options">
                            <span className="order-line-options-label">Concentration</span>
                            <div className="order-option-chips">
                              {parseOptionList(line.item.concentration).map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  className={`order-option-chip${line.concentration === c ? ' order-option-chip--active' : ''}`}
                                  onClick={() => updateLine(line.key, { concentration: c })}
                                >
                                  {c}%
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="order-fulfillment">
                  <span className="order-field-label">Fulfillment</span>
                  <div className="order-segmented order-segmented--compact" role="group" aria-label="Delivery method">
                    <button
                      type="button"
                      className={`order-segment${config.method === 'Pickup' ? ' order-segment--active' : ''}`}
                      onClick={() => setConfigField({ method: 'Pickup' })}
                    >
                      Pickup
                    </button>
                    <button
                      type="button"
                      className={`order-segment${config.method === 'Delivery' ? ' order-segment--active' : ''}`}
                      onClick={() => setConfigField({ method: 'Delivery' })}
                    >
                      Delivery
                    </button>
                  </div>

                  {config.method === 'Delivery' ? (
                    <label className="order-field">
                      <span>Destination</span>
                      <select
                        className="order-input browser-default"
                        value={config.destination}
                        onChange={(e) => setConfigField({ destination: e.target.value })}
                        aria-label="Delivery destination"
                      >
                        {deliveryOptions.map((d, index) => (
                          <option
                            key={`dest-${index}-${d.value || 'other'}`}
                            value={d.value}
                          >
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label className="order-field">
                      <span>Pickup date</span>
                      <input
                        type="text"
                        className="order-input browser-default"
                        placeholder="MM/DD/YYYY"
                        value={config.pickupDate}
                        onChange={(e) => setConfigField({ pickupDate: e.target.value })}
                      />
                    </label>
                  )}

                  {config.recipientType === 'customer' ? (
                    <label className="order-field order-field--checkbox">
                      <input
                        type="checkbox"
                        checked={config.charge}
                        onChange={(e) => setConfigField({ charge: e.target.checked })}
                      />
                      <span>Charge order to customer</span>
                    </label>
                  ) : null}

                  <label className="order-field">
                    <span>Notes</span>
                    <textarea
                      className="order-input order-input--textarea browser-default"
                      rows={2}
                      placeholder="Delivery instructions, special requests…"
                      value={config.notes}
                      onChange={(e) => setConfigField({ notes: e.target.value })}
                    />
                  </label>
                </div>

                <div className="order-cart-actions">
                  <button
                    type="button"
                    className="order-btn order-btn--primary order-submit-btn"
                    disabled={!cart.length}
                    onClick={handleSubmit}
                  >
                    Submit order
                  </button>
                </div>
              </aside>
            </div>
          ) : null}

          {configReady && cart.length > 0 ? (
            <>
              <button
                type="button"
                className={`order-cart-backdrop${cartSheetOpen ? ' order-cart-backdrop--visible' : ''}`}
                aria-label="Close cart"
                onClick={closeCartSheet}
                tabIndex={cartSheetOpen ? 0 : -1}
              />
              {!cartSheetOpen ? createPortal(
                <div className="order-mobile-bar">
                  <button type="button" className="order-mobile-bar-summary" onClick={openCartSheet}>
                    <span className="order-mobile-bar-count">{totals.items}</span>
                    <span>{totals.items === 1 ? 'item' : 'items'} in cart</span>
                  </button>
                  <button
                    type="button"
                    className="order-btn order-btn--primary order-mobile-bar-submit"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                </div>,
                document.body
              ) : null}
            </>
          ) : null}
        </>
      )}

      {productInfoId ? <ProductInfoModal close={closeProductInfo} /> : null}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    loading: state.loading,
    loadingMessage: state.loadingMessage,
    orderProducts: state.orderProducts,
    accounts: state.accounts || state.customers,
    activeOrderDraft: state.activeOrderDraft,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderWizard);
